import React, { useState, useEffect } from "react";

import Axios from "axios";
Axios.defaults.headers.common["X-CSRFTOKEN"] = document.querySelector('meta[name="csrf-token"]').content;

/**
 * Contexto principal del configurador de vehículos Toyota
 * Gestiona el estado global de la aplicación incluyendo:
 * - Modelos y versiones seleccionadas
 * - Datos de configuración (colores, accesorios, financiamiento)
 * - Información personal del usuario y leads
 * - Estados de simulación para diferentes opciones de pago
 */
const initialContext = {
    selectedModel: null,
    selectedVersion: null,
    // Configuración principal del catálogo
    configurator: {
        models: [],
        filteredModels: [],
        filters: {
            segments: [],
            models: [],
            motors: [],
        },
        preselectedDealerId: null,
    },
    // Detalles del vehículo seleccionado
    details: {
        colors: [],
        selectedColor: null,
    },
    // Gestión de accesorios
    accessories: {
        data: [],
        added: [],
        filtered: [],
        selectedFilter: "Todos",
        categories: [],
        selected: null,
        totalPrice: 0,
        ignore: { value: true },
    },
    // Configuración de financiamiento tradicional
    financiado: {
        car_amount: 0,
        downpayment_percentage: 31,
        number_fees: 12,
        smart_buy: true,
        sap_code: null,
        ignore: { value: true },
        status: null,
        pre_approved: null,
        simulation_changed: true,
    },
    // Configuración de Kinto One (arriendo)
    kinto_one_personal: {
        enabled: false,
        selected_months: 24,
        selected_downpayment: 0,
        selected_kilometers: 15000,
        car_data: null,
        values: [],
        api_response: null,
        ignore: { value: true },
    },
    // Datos de última preaprobación
    last_preapproval: {
        is_set: false,
        model: null,
        version: null,
        downpayment_percentage: null,
        number_fees: null,
        salary: null,
        pre_approved_result: null,
    },
    // Resumen de precios
    summary: {
        total_price: null,
        accessories: 0,
    },
    links: {},
    // Información personal del cliente
    personalData: {
        full_name: "",
        email: "",
        rut: "",
        phone_number: "",
        company_name: "",
        type: "",
    },
    // Estado de reserva
    reservo: {
        id_lead: null,
    },
    // Lead del primer paso
    first_step_lead: {
        id: null,
    },
    // Lead de Kinto: se crea una sola vez y `kinto_lead_type` recuerda con qué
    // producto ("one_personal" | "one_business") para actualizarlo después
    kinto_lead_created: false,
    kinto_lead_id: null,
    kinto_lead_type: null,
    // Integración con Salesforce
    salesforce: {
        id_lead: null,
    },
    // Datos de simulación
    simulated_data: {
        simulate_downpayment_percentage: 31,
        simulate_number_fees: 24,
        simulated_credit_data: {},
    },
    // Datos de preaprobación
    preapproval_data: {
        total_cost: 0,
        request_id: "",
        rate: 0,
    },
    // Marco temporal de compra
    purchase_timeframe: {
        options: [
            {
                id: 1,
                name: "En 1 semana",
            },
            {
                id: 2,
                name: "Entre 1 y 3 meses",
            },
        ],
        selected: null,
    },
    // Datos del vehículo usado
    usado: {
        patent: "",
        details: null,
        selectedVersion: null,
        mileage: null,
        range: null,
        checkedMaintenance: false,
    },
    selectedPrepaidMaintenancePlan: null,
    dealerships: [],
    // Arranca en true: el modal siempre pide los concesionarios al montarse, asi el
    // listado muestra el skeleton desde el primer render y no una lista vacia
    dealershipsLoading: true,
    selectedDealership: null,
    // Ubicación del usuario si aceptó el permiso del browser; null si lo rechazó
    userPosition: null,
    selectedPaymentOption: 1, // Default: Contado
    // Control de resultados de simulación por opción de pago
    simulationResults: {
        2: false, // MAF no muestra resultado
        3: false, // Kinto no muestra resultado
    },
    // Claves para forzar remount de simulaciones
    simulationKeys: {
        2: 0, // Counter para forzar remount de MAF
        3: 0, // Counter para forzar remount de Kinto
    },
    // Datos específicos para simulación MAF (v2)
    mafSimulationData: {
        // Input values
        downpayment_percentage: 40,
        number_fees: 36,
        smart_buy: true,

        // State management
        hasResult: false,
        isLoading: false,

        // API Response data (matches PaymentOptions v1 structure)
        credit_total_cost: null,
        downpayment_amount: null,
        equivalent_annual_load: null, // CAE
        future_value_guaranteed: null, // VFG - Cuota Final
        fee_value: null, // repaymentAmount - Cuota Mensual
        rate_interest: null,
        simulation_id: null,
        rate: null,

        // Additional calculated fields
        car_amount: null,
        credit_amount: null,

        // Id de la última petición aplicada. Permite descartar respuestas
        // fuera de orden (gana la última petición, no la última respuesta)
        request_id: 0,
        // Configuración sobre la que se corrió la simulación guardada:
        // { version_id, car_amount, downpayment_percentage, number_fees, smart_buy }
        simulated_for: null,

        // API error handling
        error: null,
    },
    // Datos específicos para simulación Kinto (v2)
    kintoSimulationData: {
        downpayment: 0,
        time: 24,
        kilometers: 15000,
        hasResult: false,
        resultData: null,
        isLoading: false,
        request_id: 0,
        // { version_id, downpayment, time, kilometers }
        simulated_for: null,
        error: null,
    },
    loading: true,
    inactiveModalUsed: false,
    appVersion: "v1", // Versión por defecto
    accessoriesEnabled: false, // Boolean para accesorios
    basename: "", // URL base del configurador
};

/**
 * Contexto React para el configurador
 * Proporciona estado global y funciones de actualización
 */
const ConfiguratorContext = React.createContext([{}, () => {}]);

/**
 * Proveedor del contexto del configurador
 * Inicializa datos desde APIs y gestiona el estado de la aplicación
 */
const ConfiguratorProvider = ({ children, version = "v1", accessories = false, basename = "" }) => {
    const [state, setState] = useState({
        ...initialContext,
        appVersion: version,
        accessoriesEnabled: accessories,
        basename: basename, // Agregar basename al estado
    });

    // Funciones para obtener datos desde APIs
    const getModels = () => Axios.get(`/api/vehicles/bnp/models/`);
    const getSegments = () => Axios.get(`/api/vehicles/bnp/segments/`);
    const getMotors = () => Axios.get(`/api/vehicles/bnp/motors/`);
    const getLinks = () => Axios.get(`/api/website/settings/links/`);

    // Inicialización de datos al montar el componente
    useEffect(() => {
        Promise.all([getModels(), getSegments(), getMotors(), getLinks()])
            .then((response) => {
                let models = response[0].data.map((item) => ({
                    ...item,
                    versions: item.versions.filter((version) => version.price > 0),
                }));

                let segments = response[1].data;
                let motors = response[2].data;
                let links = response[3].data;
                initData(models, segments, motors, links);
            })
            .catch((error) => {
                console.error(error);
            })
            .finally(() => {
                setState((prevState) => ({
                    ...prevState,
                    loading: false,
                }));
            });
    }, []);

    // Inicializa los filtros y opciones con los datos obtenidos de las APIs
    // Inicializa los filtros y opciones con los datos obtenidos de las APIs
    const initData = (models, segments, motors, links) => {
        let optionsSegments = segments;
        optionsSegments.unshift({ id: 0, name: "Todos" });
        let optionsMotors = motors;
        optionsMotors.unshift({ id: 0, name: "Todos" });
        let optionsModels = models.map((model) => ({ id: model.id, name: model.name }));
        optionsModels.unshift({ id: 0, name: "Todos" });

        setState((prevState) => ({
            ...prevState,
            configurator: {
                ...prevState.configurator,
                models: models,
                filteredModels: models,
                filters: {
                    segments: optionsSegments,
                    models: optionsModels,
                    motors: optionsMotors,
                },
            },
            details: {
                ...prevState.details,
            },
            summary: {
                ...prevState.summary,
            },
            links,
        }));
    };

    return (
        <ConfiguratorContext.Provider value={[state, setState]}>
            {children}
        </ConfiguratorContext.Provider>
    );
};

export { ConfiguratorContext, ConfiguratorProvider, initialContext };
